import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid-premium';
import { reportService } from '../../api';
import { Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchPortfolioTotalsReport } from '../../redux/modules/report/report.actions';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import type { PortfolioTotalsReportApiItem, ReportApiDateRangeQuery } from '../../types/report';
import { gridFormatDecimalString } from './reportGridFormatters';
import { ReportDateRangeFilter } from './ReportDateRangeFilter';
import { useReportCsvExport } from './useReportCsvExport';

function portfolioTotalsRowId(row: PortfolioTotalsReportApiItem) {
  return `${row.user_id}-${row.portfolio_id}-${row.subscribed_portfolio_id}-${row.date}`;
}

export function PortfolioTotalsReportPage() {
  const [dateRange, setDateRange] = useState<ReportApiDateRangeQuery | null>(null);
  const onDateRangeChange = useCallback((r: ReportApiDateRangeQuery | null) => {
    setDateRange(r);
  }, []);

  const columns = useRef<GridColDef[]>([
    { field: 'user_id', headerName: 'User Id', type: 'number', width: 100 },
    { field: 'portfolio_id', headerName: 'Portfolio Id', type: 'number', width: 120 },
    { field: 'subscribed_portfolio_id', headerName: 'Subscribed Id', type: 'number', width: 130 },
    { field: 'portfolio_name', headerName: 'Portfolio name', minWidth: 180, flex: 0.4 },
    { field: 'portfolio_status', headerName: 'Status', width: 120 },
    { field: 'date', headerName: 'Date', type: 'date', width: 130 },
    {
      field: 'total_market_value',
      headerName: 'Total market value',
      width: 170,
      valueFormatter: gridFormatDecimalString,
    },
    { field: 'total_return', headerName: 'Total return', width: 140, valueFormatter: gridFormatDecimalString },
    { field: 'net_deposit', headerName: 'Net deposit', width: 150, valueFormatter: gridFormatDecimalString },
    { field: 'net_withdrawals', headerName: 'Net withdrawals', width: 150, valueFormatter: gridFormatDecimalString },
    { field: 'total_amount', headerName: 'Total amount', width: 150, valueFormatter: gridFormatDecimalString },
    { field: 'portfolio_size', headerName: 'Portfolio size', width: 150, valueFormatter: gridFormatDecimalString },
    { field: 'created_at', headerName: 'Created', type: 'dateTime', width: 180 },
    { field: 'updated_at', headerName: 'Updated', type: 'dateTime', width: 180 },
  ]);

  const { portfolioTotalsReportItems, totalPortfolioTotalsReportItems, fetchPortfolioTotalsReportStatus, error } =
    useSelector(reportSelector);

  const { exportCsv, loading: exportCsvLoading } = useReportCsvExport(
    'portfolio-totals-report.csv',
    useCallback(() => reportService.exportPortfolioTotalsReportCsv(), []),
  );

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      <Grid container flexDirection="column" component={Paper} sx={{ p: 3, mt: 0 }}>
        <ReportDateRangeFilter onRangeChange={onDateRangeChange} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6">Portfolio totals</Typography>
          <LoadingButton
            type="button"
            variant="outlined"
            color="primary"
            endIcon={<DownloadIcon />}
            loading={exportCsvLoading}
            loadingPosition="end"
            onClick={exportCsv}
          >
            Export all CSV
          </LoadingButton>
        </Stack>
        <Table
          wrapperHeight={700}
          fetchItems={fetchPortfolioTotalsReport}
          fetchItemsCustomParams={dateRange ?? {}}
          rows={portfolioTotalsReportItems}
          columns={columns.current}
          getRowId={portfolioTotalsRowId}
          loading={fetchPortfolioTotalsReportStatus === fetchStatuses.pending}
          rowCount={totalPortfolioTotalsReportItems}
          initialSortModel={[{ field: 'date', sort: 'desc' }]}
        />
      </Grid>
    </>
  );
}
